import React, { useState } from 'react';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle, Mail, MessageSquare, HelpCircle } from 'lucide-react';
import api from '../../lib/api';
import { useCanonical } from '../../hooks/useCanonical';

function ContactPage() {
  useCanonical('/contact');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.submitContactForm(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 py-16 md:py-24">
          <div className="section-container max-w-2xl">
            <Card className="card-base">
              <CardContent className="p-8 md:p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-emerald-400" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">Message Sent!</h1>
                <p className="text-slate-400 mb-8">
                  Thank you for reaching out. We'll get back to you as soon as possible.
                </p>
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                  }}
                  className="btn-secondary"
                >
                  Send Another Message
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      <Navbar />
      <main className="flex-1 py-16 md:py-20 relative">
        <div className="absolute inset-x-0 top-0 h-72 pointer-events-none dark:hidden" style={{ background: 'radial-gradient(700px 240px at 50% 0%, hsl(226 100% 55% / 0.07), transparent 70%)' }} />
        <div className="absolute inset-x-0 top-0 h-72 pointer-events-none hidden dark:block" style={{ background: 'radial-gradient(800px 280px at 50% 0%, hsl(226 100% 55% / 0.10), transparent 70%)' }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-3">◢ GET IN TOUCH</div>
            <h1 className="font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(2rem, 4vw, 3.4rem)' }}>
              Say <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={{ fontFamily: '"Instrument Serif"' }}>hello.</span>
            </h1>
            <p className="mt-4 text-base text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed max-w-xl mx-auto">
              Have a question or need assistance? We're here to help — typically within 24 hours.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-4">
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">General Inquiries</h3>
                      <p className="text-slate-400 text-sm">Questions about Servio or our services</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Technical Support</h3>
                      <p className="text-slate-400 text-sm">Help with your account or profile</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Response Time</h3>
                      <p className="text-slate-400 text-sm">We typically respond within 24 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <Card className="card-base">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300">
                          Name <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          className="input-base"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">
                          Email <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="input-base"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">
                          Phone <span className="text-slate-500">(optional)</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          className="input-base"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-slate-300">
                          Subject <span className="text-red-400">*</span>
                        </Label>
                        <Select value={formData.subject} onValueChange={handleSubjectChange}>
                          <SelectTrigger className="input-base" data-testid="contact-subject-select">
                            <SelectValue placeholder="Select a topic" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="technical">Technical Support</SelectItem>
                            <SelectItem value="billing">Billing Question</SelectItem>
                            <SelectItem value="feedback">Feedback</SelectItem>
                            <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">
                        Message <span className="text-red-400">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        className="input-base min-h-[150px] resize-none"
                        required
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="btn-accent w-full sm:w-auto"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default ContactPage;
